import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {
  selectCurrentPlayer,
  selectGameResults,
  selectGameType,
  selectIsGameStarted,
  selectPendingMeleeDamage,
  selectPhase,
  selectPlayers,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {GAME_TYPES} from "../../constants/game";
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import store from "../../redux/store";
import {executeBotStep} from "../../utils/bots/executeBotStep";
import {BOT_ACTION_INTERVAL_MS, shouldUseBotStrongMode} from "../../utils/bots/botConfig";
import {setBotActionIntervalMs} from "../../utils/bots/botActionPace";
import {selectIsFieldCutscenePlaying} from "../../redux/reducers/privateStateReducer/selectors";

/**
 * Движок ботов для локальной игры.
 * Пока ход принадлежит боту, выполняет по одному видимому действию каждые
 * actionIntervalMs (по умолчанию BOT_ACTION_INTERVAL_MS; в демо-партии меню
 * интервал уменьшается выбранным пользователем множителем скорости).
 * Каждый тик перепроверяет условия на свежем состоянии store —
 * авто-действия (PhaseAutoComplitioner) могут продвинуть игру между тиками.
 */
const BotRunner = ({actionIntervalMs = BOT_ACTION_INTERVAL_MS}) => {
  const gameType = useSelector(selectGameType);
  const isGameStarted = useSelector(selectIsGameStarted);
  const gameResults = useSelector(selectGameResults);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);

  const players = useSelector(selectPlayers);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  // Пока поле показывает заставку (облет захваченных городов, рост дохода
  // руин), боты ждут: их ходы шли бы за кадром, пока игрок ее смотрит
  const isFieldCutscenePlaying = useSelector(selectIsFieldCutscenePlaying);

  const timerRef = useRef(null);

  const isBotTurnActive = gameType === GAME_TYPES.LOCAL
    && isGameStarted
    && !gameResults
    && Boolean(currentPlayer?.isBot);

  // Режим защитника: этап ближнего боя живого игрока, в котором участвуют
  // отряды ботов — боты делают перебросы/приоритеты в свои обычные тики
  const meleeOwnerIds = new Set(pendingMeleeDamage.map((action) => action.ownerId));
  const isBotMeleeDefenderActive = gameType === GAME_TYPES.LOCAL
    && isGameStarted
    && !gameResults
    && !currentPlayer?.isBot
    && phase === PHASES.WAR
    && warPhaseStage === WAR_PHASE_STAGES.COMBAT
    && players.some((player) => player.isBot && meleeOwnerIds.has(player.id));

  const isEngineActive = (isBotTurnActive || isBotMeleeDefenderActive) && !isFieldCutscenePlaying;

  const currentPlayerId = currentPlayer?.id;

  // Шаг нужен сцене, а до нее пропсы не дотягиваются: анимация действия бота
  // должна уложиться в паузу до следующего (см. utils/bots/botActionPace)
  useEffect(() => {
    setBotActionIntervalMs(actionIntervalMs);

    return () => setBotActionIntervalMs(BOT_ACTION_INTERVAL_MS);
  }, [actionIntervalMs]);

  useEffect(() => {
    if (!isEngineActive) {
      return;
    }

    let cancelled = false;

    const tick = () => {
      if (cancelled) {
        return;
      }

      // Сильный режим включен в локальных играх (и в демо-партии меню, которая
      // использует этот же компонент) по умолчанию, кроме фракций, которым
      // он по данным симуляций вредит (BOT_STRONG_MODE_DISABLED_FACTIONS,
      // developer_artifacts/factions_rules.md) — обычный режим также сохранен
      // для симуляций (см. server/scripts/simulateBotBatch.js)
      const actingPlayer = selectCurrentPlayer(store.getState());
      executeBotStep(store, {strongMode: shouldUseBotStrongMode(actingPlayer?.factionId)});

      // Самоперепланирование: цикл жив, даже если шаг не изменил состояние.
      // Фаза расстановки скрыта от глаз — боты ходят в ней мгновенно;
      // интервал нужен только чтобы люди успевали отслеживать действия бота
      const nextDelay = selectPhase(store.getState()) === PHASES.SETUP
        ? 0
        : actionIntervalMs;
      timerRef.current = setTimeout(tick, nextDelay);
    };

    // Фазы снабжения (авто-раздача + завершение — один шаг) и расстановки
    // (деятельность ботов скрыта от игрока) начинаются МГНОВЕННО; интервал
    // нужен только между видимыми действиями остальных фаз
    const initialDelay = phase === PHASES.SUPPLY || phase === PHASES.SETUP
      ? 0
      : actionIntervalMs;
    timerRef.current = setTimeout(tick, initialDelay);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [isEngineActive, currentPlayerId, phase, warPhaseStage, actionIntervalMs]);

  return null;
};

export default BotRunner;
